import type { CmsFamilyPtyContent } from '../types/websiteCms.types';

/**
 * Drafted defaults for the unlisted /family-pty lander (Jaco 2026-10-02),
 * copied from "Website layout - Group.pdf". Every section is overridable in
 * Website CMS → Family Pty (websiteContent/familyPty); missing sections fall
 * back to these so the page always renders.
 */
export const DEFAULT_FAMILY_PTY: CmsFamilyPtyContent = {
  seo: {
    metaTitle: 'Family Pty | Buy Property With Your People | PropInvest',
    metaDescription:
      'Buy an investment property with 2 to 6 family members or friends you already trust, through a company PropInvest registers for you free.',
  },
  hero: {
    image: '',
    heading: 'Your people. One company. We register it free.',
    subheading: 'Buy an investment property with the family or friends you already trust.',
    enabled: true,
  },
  video: {
    heading: 'How Family Pty works',
    entry: { type: 'upload', uploadedUrl: '', thumbnailUrl: '' },
    enabled: true,
  },
  formSection: {
    heading: 'Setup my family or friends PTY',
    subtext: 'Complete the form and we will send you all the information via WhatsApp.',
    whatsappNote: 'We will send you all the information via WhatsApp.',
    disclaimer: '',
    enabled: true,
  },
  document: {
    title: 'Setup my family or friends PTY',
    description: 'Download the Family Pty guide: how buying with your people through one company works.',
    url: '',
    fileName: '',
    enabled: true,
  },
  intro: {
    paragraphs: [
      'You do not have to invest in property alone! South Africans buy with family. They will not buy with strangers. PropInvest has built the structure for your family or friends. Our service is completely free of charge and obligation free.',
      'Bring 2 to 6 family members or friends, who you already trust. Every person in your group gets shares. Now you don’t have to invest alone, your company with family or friends buys the investment.',
      'This gives more South Africans access to cashflow positive property investments.',
    ],
    highlight: 'The GROUP Millionaire Strategy! Your people, Your company. We register it for free.',
    enabled: true,
  },
  cta: {
    buttonText: 'Complete The Form To Get Started',
    caption: 'Do not buy with strangers. Buy with your people. PropInvest registers the Family Pty free.',
  },
  why: {
    heading: 'Why this exists',
    paragraphs: [
      'Most good developments fail on one salary and one deposit. The same deal often works when two to six people you already trust buy through one company.',
      'Family Pty is that company. You choose the people. We build the structure. You buy the same exclusive, cashflow focused stock we already offer.',
    ],
    enabled: true,
  },
  how: {
    heading: 'How it works',
    steps: [
      { title: 'Bring your people', text: 'Spouse, siblings, parents, adult children, or close friends. Two to six names.' },
      { title: 'We register the Family Pty', text: 'Free. Shares in each person’s name. PropInvest is not a shareholder.' },
      { title: 'The company buys', text: 'One buyer. One offer. One investment. Each person owns a slice of the company that owns the unit.' },
      { title: 'You get the plan in writing', text: 'A WhatsApp summary with the split, the deposit, and the numbers for this development.' },
    ],
    enabled: true,
  },
  included: {
    heading: 'What you get, for free',
    items: [
      'Company registration',
      'Shareholding set up in each name',
      'A simple structure built so the company can buy',
      'Your Family Pty summary on WhatsApp',
      'A consultant to walk the offer with the group',
    ],
    enabled: true,
  },
  stillPay: {
    heading: 'What you still pay for',
    text: 'The property. The deposit into the company. Normal purchase costs that apply to the deal.',
    enabled: true,
  },
  whoFor: {
    heading: 'Who this is for',
    items: [
      'A couple who want one investment, two names protected',
      'Siblings helping each other onto the first unit',
      'Parents and an adult child buying together',
      'Two or three friends who would already go into business together',
      'A lead who wants the development, but cannot carry it alone',
    ],
    enabled: true,
  },
  whoNotFor: {
    heading: 'Who this is not for',
    items: [
      'People you have not met',
      'A large club of investors',
      'Anyone you would not trust with a shared bank account',
    ],
    enabled: true,
  },
  compare: {
    heading: 'Compare the two paths',
    alone: { title: 'Buy in one name', text: 'One deposit. One income. One person carries the full unit.' },
    group: { title: 'Buy through a Family Pty', text: 'The group’s deposit. The group’s capacity. Shares in each name. Same development.' },
    enabled: true,
  },
  trust: {
    heading: 'Trust line',
    text: 'PropInvest has specialised in exclusive property investments since 2002. Family Pty sits inside that same process. PPRA registered. PropInvest 5 (Pty) Ltd. We set up the company. We do not own it.',
    enabled: true,
  },
  faq: {
    heading: 'FAQ - Frequently asked questions',
    items: [
      { question: 'Do I own the flat in my personal name?', answer: 'The Family Pty owns the unit. You own shares in the Family Pty. That is the clean way to hold a group purchase.' },
      { question: 'Can we choose the split?', answer: 'Yes. Equal shares, or a split that matches what each person puts in.' },
      { question: 'Is this the same as buying with strangers?', answer: 'No. You bring the only people who will ever be in the company.' },
      { question: 'How fast can you set it up?', answer: 'As soon as the group is confirmed and the documents are in. The purchase does not wait on a complicated scheme.' },
      { question: 'What if we want a trust instead?', answer: 'Say so. If a trust is the better vehicle for your family, we will structure that path instead.' },
    ],
    enabled: true,
  },
  thankYou: {
    heading: 'Thank you! We have your details.',
    message: 'We will send you all the information via WhatsApp shortly. Download your Family Pty guide below.',
    downloadButton: 'Download your Family Pty guide',
  },
};

/** Section merge that ignores null/undefined overlay values (see useEducationCms). */
function mergeSection<T extends object>(defaults: T, overlay: Partial<T> | undefined | null): T {
  const out: T = { ...defaults };
  if (!overlay || typeof overlay !== 'object') return out;
  for (const [k, v] of Object.entries(overlay)) {
    if (v === undefined || v === null) continue;
    const def = (defaults as Record<string, unknown>)[k];
    if (Array.isArray(def) && !Array.isArray(v)) continue;
    (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/** Pure: a saved CMS doc over the drafted defaults, section by section. */
export function mergeFamilyPty(d: Partial<CmsFamilyPtyContent> | null | undefined): CmsFamilyPtyContent {
  const D = DEFAULT_FAMILY_PTY;
  if (!d) return D;
  return {
    seo: mergeSection(D.seo, d.seo),
    hero: mergeSection(D.hero, d.hero),
    video: { ...mergeSection(D.video, d.video), entry: mergeSection(D.video.entry, d.video?.entry) },
    formSection: mergeSection(D.formSection, d.formSection),
    document: mergeSection(D.document, d.document),
    intro: mergeSection(D.intro, d.intro),
    cta: mergeSection(D.cta, d.cta),
    why: mergeSection(D.why, d.why),
    how: mergeSection(D.how, d.how),
    included: mergeSection(D.included, d.included),
    stillPay: mergeSection(D.stillPay, d.stillPay),
    whoFor: mergeSection(D.whoFor, d.whoFor),
    whoNotFor: mergeSection(D.whoNotFor, d.whoNotFor),
    compare: {
      ...mergeSection(D.compare, d.compare),
      alone: mergeSection(D.compare.alone, d.compare?.alone),
      group: mergeSection(D.compare.group, d.compare?.group),
    },
    trust: mergeSection(D.trust, d.trust),
    faq: mergeSection(D.faq, d.faq),
    thankYou: mergeSection(D.thankYou, d.thankYou),
  };
}
